import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

export const ROLES = { ADULT: 'adult', MINOR: 'minor' };

// Escalating fines (in ₹) for violations 1..4. Violation #5 => permanent disable.
export const FINES = [300, 500, 1000, 2000];
export const MAX_CHANCES = 5;

const EMOJI_RE = /\p{Extended_Pictographic}/u;
const IRRELEVANT = [
  'meet me', 'meet up', 'address', 'where do you live', 'send pic', 'send photo',
  'selfie', 'alone', 'secret', 'phone number', 'come over', 'my place', 'hangout',
];

// Returns { flagged, severity: 'disable'|'warn', reason }
export function moderateMessage(text, senderRole, recipientRole) {
  const lower = (text || '').toLowerCase();
  const hasEmoji = EMOJI_RE.test(text || '');
  const hasIrrelevant = IRRELEVANT.some((k) => lower.includes(k));
  const flagged = hasEmoji || hasIrrelevant;
  if (!flagged) return { flagged: false };

  const kind = hasEmoji && !hasIrrelevant ? 'emoji' : 'irrelevant / unsafe text';

  // Adult -> Minor: zero tolerance, no warning
  if (senderRole === ROLES.ADULT && recipientRole === ROLES.MINOR) {
    return { flagged: true, severity: 'disable', reason: `Adult sent ${kind} to a minor` };
  }
  // Minor -> Minor: chances
  if (senderRole === ROLES.MINOR && recipientRole === ROLES.MINOR) {
    return { flagged: true, severity: 'warn', reason: `Minor-to-minor ${kind}` };
  }
  // Everything else allowed in this demo
  return { flagged: false };
}

const SafetyContext = createContext(null);
export const useSafety = () => useContext(SafetyContext);

const load = (k, d) => {
  try { const v = localStorage.getItem(k); return v === null ? d : JSON.parse(v); } catch { return d; }
};

export function SafetyProvider({ children }) {
  const [myRole, setMyRole] = useState(() => load('emily_myRole', ROLES.ADULT));
  const [violations, setViolations] = useState(() => load('emily_violations', 0));
  const [locked, setLocked] = useState(() => load('emily_locked', false));
  const [disabled, setDisabled] = useState(() => load('emily_disabled', false));
  const [lastReason, setLastReason] = useState('');

  useEffect(() => { localStorage.setItem('emily_myRole', JSON.stringify(myRole)); }, [myRole]);
  useEffect(() => { localStorage.setItem('emily_violations', JSON.stringify(violations)); }, [violations]);
  useEffect(() => { localStorage.setItem('emily_locked', JSON.stringify(locked)); }, [locked]);
  useEffect(() => { localStorage.setItem('emily_disabled', JSON.stringify(disabled)); }, [disabled]);

  // Evaluate an outgoing message. Returns true if it's allowed to send.
  const evaluate = useCallback((text, peerRole) => {
    const res = moderateMessage(text, myRole, peerRole);
    if (!res.flagged) return { allowed: true };

    if (res.severity === 'disable') {
      setLastReason(res.reason);
      setDisabled(true);
      return { allowed: false, action: 'disable', reason: res.reason };
    }
    // warn / chance-based (minor -> minor)
    const next = violations + 1;
    setViolations(next);
    setLastReason(res.reason);
    if (next >= MAX_CHANCES) {
      setDisabled(true);
      return { allowed: false, action: 'disable', reason: res.reason, count: next };
    }
    setLocked(true);
    return { allowed: false, action: 'lock', reason: res.reason, count: next };
  }, [myRole, violations]);

  const unlock = useCallback(() => setLocked(false), []);

  const resetDemo = useCallback(() => {
    setViolations(0); setLocked(false); setDisabled(false); setLastReason('');
  }, []);

  const currentFine = FINES[Math.min(violations - 1, FINES.length - 1)] || FINES[0];

  const value = useMemo(() => ({
    myRole, setMyRole, violations, locked, disabled, lastReason,
    evaluate, unlock, resetDemo, currentFine,
  }), [myRole, violations, locked, disabled, lastReason, evaluate, unlock, resetDemo, currentFine]);

  return (
    <SafetyContext.Provider value={value}>
      {children}
    </SafetyContext.Provider>
  );
}
